import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatNaira } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();

  const [form, setForm] = useState({ name: "", phone: "", password: "", confirm_password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Welcome bonus amount is admin-controlled — read from public settings so
  // the pre-signup copy (subtitle + badge) never drifts from what the backend
  // will actually credit. Falls back to a sane placeholder while loading.
  const [welcomeBonus, setWelcomeBonus] = useState(null);

  useEffect(() => {
    const ref = sp.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
    api.get("/settings/public")
      .then((r) => setWelcomeBonus(Number(r.data?.welcome_bonus) || 0))
      .catch(() => setWelcomeBonus(0));
  }, [sp]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    // Passwords must match before we hit the backend — cheaper feedback
    // and stops accidental typos from creating an account the user can't
    // sign back into.
    if (form.password !== form.confirm_password) {
      setErr("Passwords don't match. Re-type both to continue.");
      setLoading(false);
      return;
    }
    try {
      const { confirm_password, ...payload } = form;
      const { user, welcome_bonus_credited } = await register(payload);
      // Prefer the amount the backend actually credited; fall back to settings.
      const credited = Number(welcome_bonus_credited ?? welcomeBonus ?? 0);
      toast.success(`Account created! ${formatNaira(credited)} welcome bonus credited 🎉`);
      nav(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (e2) {
      setErr(e2.message);
    } finally { setLoading(false); }
  };

  // Display value while typing (before /settings/public resolves) — shows "…"
  const bonusLabel = welcomeBonus === null ? "…" : formatNaira(welcomeBonus);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--nb-page)]">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <h2 className="font-display text-3xl font-800 tracking-tight">Create your account</h2>
          <p className="text-[var(--nb-muted)] mt-2" data-testid="register-subtitle">Sign up in seconds. Claim your {bonusLabel} welcome bonus.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="text-[var(--nb-text)]">Full name</Label>
              <Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} required
                     className="mt-2 bg-[var(--nb-card2)] border-[var(--nb-border)] text-white h-11" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-[var(--nb-text)]">Phone number</Label>
              <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={set("phone")} required
                     placeholder="08012345678"
                     className="mt-2 bg-[var(--nb-card2)] border-[var(--nb-border)] text-white placeholder:text-[var(--nb-muted)]/60 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[var(--nb-text)]">Password</Label>
              <Input id="password" data-testid="register-password-input" type="password" value={form.password}
                     onChange={set("password")} required minLength={6}
                     className="mt-2 bg-[var(--nb-card2)] border-[var(--nb-border)] text-white h-11" />
              <p className="text-xs text-[var(--nb-muted)] mt-1">At least 6 characters.</p>
            </div>
            <div>
              <Label htmlFor="confirm_password" className="text-[var(--nb-text)]">Confirm password</Label>
              <Input id="confirm_password" data-testid="register-confirm-password-input" type="password"
                     value={form.confirm_password} onChange={set("confirm_password")} required minLength={6}
                     className={`mt-2 bg-[var(--nb-card2)] text-white h-11 ${
                       form.confirm_password && form.confirm_password !== form.password
                         ? "border-[#EF4444]"
                         : "border-[var(--nb-border)]"
                     }`} />
              {form.confirm_password && form.confirm_password !== form.password ? (
                <p data-testid="register-confirm-mismatch" className="text-xs text-[#EF4444] mt-1">
                  Passwords don't match.
                </p>
              ) : (
                <p className="text-xs text-[var(--nb-muted)] mt-1">Re-type the same password.</p>
              )}
            </div>
            <div>
              <Label htmlFor="referral_code" className="text-[var(--nb-text)]">Referral code <span className="text-[var(--nb-muted)]">(optional)</span></Label>
              <Input id="referral_code" data-testid="register-referral-input" value={form.referral_code}
                     onChange={set("referral_code")}
                     className="mt-2 bg-[var(--nb-card2)] border-[var(--nb-border)] text-white h-11 uppercase" />
            </div>
            {err && <div data-testid="register-error" className="text-sm text-[#EF4444]">{err}</div>}
            <Button type="submit"
                    disabled={loading || (form.confirm_password.length > 0 && form.confirm_password !== form.password)}
                    data-testid="register-submit-button"
                    className="w-full h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-md glow-primary">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-[var(--nb-muted)] mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login-link" className="text-[#0055FF] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-12 border-l border-[var(--nb-border)] relative overflow-hidden order-1 lg:order-2">
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#0055FF]/20 blur-[140px]" />
        <Link to="/" className="flex items-center gap-2 relative z-10">
          <BrandLogo size={36} />
          <span className="font-display font-bold text-xl">Luckycart Box</span>
        </Link>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0055FF]/15 border border-[#0055FF]/40 text-xs text-white mb-6" data-testid="register-bonus-badge">
            <Gift className="w-3 h-3" /> {bonusLabel} welcome bonus
          </div>
          <h1 className="font-display text-4xl font-800 tracking-tight leading-tight">
            Nigeria's smartest way to grow your naira.
          </h1>
          <p className="text-[var(--nb-muted)] mt-4 max-w-md">
            Automated daily profits, 3-generation referrals, instant bank withdrawals.
          </p>
        </div>
        <div className="text-xs text-[var(--nb-muted)] relative z-10">© 2026 Luckycart Box</div>
      </div>
    </div>
  );
}
